import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useSelector } from "react-redux";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Input } from "@progress/kendo-react-inputs";
import { number } from "../../../../config";
import { useWorkflowContext } from "../../WorkflowContextProvider";
import { useVariablesContext } from "../context/variableContext";
import { addOrUpdateWorkflowExpression, getWorkflowExpressionByVariableId } from "../services/workflowVariables.service";
import { convertExpressionIdsToNames, convertExpressionNamesToIds } from "../utils/expressionHelper";
import "../workflowVariable.scss";
import { VARIABLE_ICONS, WORKFLOW_VARIABLE_LABELS } from "../workflowVariables.constants";
import { regex, label, placeholder } from "../../../../config/constants";
import { EXPRESSION_REGEX, OPERATOR_CHAR_REGEX } from "../../../../config/regex";

/**
 * ExpressionEditor component for building expressions using smart chips and operators
 * @author Muskan Thakur
 */
const ExpressionEditor = ({
  isOpen,
  onClose,
  onSave,
  currentVariableName,
  expectedVariableType = "number",
  selectedVariable,
  workflow,
  isNewWorkflowVariable,
  onSaveVariable,
}) => {
  const { user } = useSelector((state) => state.auth);
  const { customWorkflowVariables, setSelectedVariable, formId } = useWorkflowContext();
  const { expressionValue, setExpressionValue, setHasVarChanges } = useVariablesContext();

  const [localExpression, setLocalExpression] = useState(expressionValue || "");
  const [cursorPosition, setCursorPosition] = useState(0);
  const [isValid, setIsValid] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const expressionInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredItemIndex, setHoveredItemIndex] = useState(null);
  const containerRef = useRef(null);
  const pendingCursorPosition = useRef(null);
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);

  const availableSmartChips = customWorkflowVariables || [];
  const whiteSpacePreStyle = { whiteSpace: "pre"};
  const hiddenStyle = { visibility: "hidden" };

  /**
   * Gets the sort priority for a variable
   * Dropdowns are sorted by numberOnly attribute (string-based first, then numeric)
   * Order: String-based dropdown → Numeric dropdown → Text → Number → Date
   */
  const getVariablePriority = (variable) => {
    const type = variable?.type;
    if (type === "dropdown") {
      return variable?.attributes?.numberOnly ? number.ONE : number.ZERO;
    }
    // Other types: text → 2, number → 3, date → 4
    const typePriority = {
      text: number.TWO,
      number: number.THREE,
      date: number.FOUR,
    };
    return typePriority[type] ?? Number.MAX_SAFE_INTEGER;
  };

  /**
   * Sorts smart chips by type priority and then alphabetically
   */
  const sortSmartChips = (chips) => {
    if (!chips) return [];
    return [...chips].sort((a, b) => {
      const priorityA = getVariablePriority(a);
      const priorityB = getVariablePriority(b);
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      return (a?.name || "").localeCompare(b?.name || "");
    });
  };

  /** Filter smart chips based on search query and sort them */
  const filteredSmartChips = useMemo(() => {
    const filtered = availableSmartChips.filter((chip) => chip.name?.toLowerCase().includes(searchQuery.toLowerCase()));
    return sortSmartChips(filtered);
  }, [availableSmartChips, searchQuery]);

  /** Operators list */
  const operators = [
    { id: "add", symbol: "+", label: "Add" },
    { id: "subtract", symbol: "-", label: "Subtract" },
    { id: "multiply", symbol: "*", label: "Multiply" },
    { id: "divide", symbol: "/", label: "Divide" },
  ];

  /** Parse expression to extract items - shared helper for both cursor positioning and validation */
  const parseExpressionHelper = useCallback((expression) => {
    if (!expression) return [];

    const items = [];
    let currentIndex = 0;
    const regexPattern = new RegExp(EXPRESSION_REGEX.source, EXPRESSION_REGEX.flags);
    let match;
    let lastMatchEnd = 0;

    while ((match = regexPattern.exec(expression)) !== null) {
      if (match.index > currentIndex) {
        const text = expression.substring(currentIndex, match.index);
        items.push({ type: label.TEXT , value: text, start: currentIndex, end: match.index });
      }

      if (match[0].startsWith("[")){
        items.push({
          type: label.SMARTCHIP,
          value: match[0],
          name: match[1],
          start: match.index,
          end: match.index + match[0].length,
        });
      } else {
        items.push({
          type: label.OPERATOR,
          value: match[0],
          start: match.index,
          end: match.index + match[0].length,
        });
      }

      currentIndex = match.index + match[0].length;
      lastMatchEnd = currentIndex;
    }

    if (lastMatchEnd < expression.length) {
      const text = expression.substring(lastMatchEnd);
      items.push({ type: label.TEXT, value: text, start: lastMatchEnd, end: expression.length });
    }

    if (items.length === 0 && expression) {
      items.push({ type: label.TEXT, value: expression, start: 0, end: expression.length });
    }

    return items;
  }, []);

  /** Parse expression to extract smart chips and operators */
  const expressionItems = useMemo(() => {
    return parseExpressionHelper(localExpression);
  }, [localExpression, parseExpressionHelper]);

  /** Helper to update state for empty expressions */
  const handleEmptyExpression = useCallback(() => {
    setErrorMessage("");
    setIsValid(true);
  }, []);

  /** Helper to mark expression as changed */
  const markExpressionChanged = useCallback(() => {
    setHasChanges(true);
    setIsValidated(false);
  }, []);

  /** Helper to get current cursor position */
  const getCurrentCursorPosition = useCallback(() => {
    const input = expressionInputRef.current;
    if (!input) return localExpression.length;
    return input.selectionStart !== null ? input.selectionStart : cursorPosition || localExpression.length;
  }, [localExpression, cursorPosition]);

  /** Helper to count spaces at a position */
  const countSpaces = useCallback((expression, position) => {
    let spaceCount = 0;
    let checkPos = position;
    while (checkPos < expression.length && expression[checkPos] === ' ') {
      spaceCount++;
      checkPos++;
    }
    return spaceCount;
  }, []);

  /** Helper to set validation error */
  const setValidationError = useCallback(() => {
    setIsValid(false);
    setErrorMessage(label.EXPRESSION_EDITOR_ERROR_IN_EXPRESSION);
  }, []);

  /** Ensure cursor is never inside a chip or operator */
  const ensureCursorOutsideChips = useCallback((expression, position) => {
    if (!expression) return { expression, position: expression ? expression.length : 0 };

    const items = parseExpressionHelper(expression);
    
    for (const item of items) {
      if (item.type === label.SMARTCHIP || item.type === label.OPERATOR) {
        if (position > item.start && position <= item.end) {
          let newPosition = item.end;
          let newExpression = expression;
          
          const spaceCount = countSpaces(expression, newPosition);
          
          if (spaceCount < number.TWO) {
            const spacesToAdd = number.TWO - spaceCount;
            const before = expression.substring(0, item.end);
            const after = expression.substring(item.end);
            newExpression = before + ' '.repeat(spacesToAdd) + after;
            newPosition = item.end + spacesToAdd;
          } else {
            newPosition = item.end + spaceCount;
          }
          
          return { expression: newExpression, position: newPosition };
        }
      }
    }
    
    return { expression, position };
  }, [parseExpressionHelper, countSpaces]);

  /** Ensure spaces before cursor position for better visibility */
  const ensureSpacesBeforeCursor = useCallback((expression, position) => {
    if (!expression || position <= 0) return { expression, position };

    const { expression: safeExpression, position: safePosition } = ensureCursorOutsideChips(expression, position);
    
    const charBefore = safeExpression[safePosition - 1];
    
    if (charBefore === ']' || OPERATOR_CHAR_REGEX.test(charBefore)) {
      const spaceCount = countSpaces(safeExpression, safePosition);
      
      if (spaceCount < number.TWO) {
        const spacesToAdd = number.TWO - spaceCount;
        const before = safeExpression.substring(0, safePosition);
        const after = safeExpression.substring(safePosition);
        const newExpression = before + ' '.repeat(spacesToAdd) + after;
        const newPosition = safePosition + spacesToAdd;
        return { expression: newExpression, position: newPosition };
      }
    }
    
    return { expression: safeExpression, position: safePosition };
  }, [ensureCursorOutsideChips, countSpaces]);

  /** Initialize local expression when dialog opens */
  useEffect(() => {
    if (!isOpen) return;

    const loadExpression = async () => {
      let expressionToUse = expressionValue || "";

      if (selectedVariable?.id && !isNewWorkflowVariable && workflow?.Id) {
        const params = {
          workflowVariableId: selectedVariable.id,
        };
        if (formId) {
          params.formId = formId;
        }
        const expressionData = await getWorkflowExpressionByVariableId(params);
        if (expressionData) {
          expressionToUse = expressionData?.expression || expressionData?.rawExpression || expressionValue || "";
        }
      }

      const expressionWithNames = convertExpressionIdsToNames(expressionToUse, customWorkflowVariables);
      setLocalExpression(expressionWithNames);
      setExpressionValue(expressionWithNames);
      setHasChanges(false);
      setIsValidated(false);
      setErrorMessage("");
      setIsValid(true);
      
      const endPosition = expressionWithNames.length;
      const { expression: safeExpression, position: safePosition } = ensureCursorOutsideChips(expressionWithNames, endPosition);
      if (safeExpression !== expressionWithNames) {
        setLocalExpression(safeExpression);
        setExpressionValue(safeExpression);
      }
      setCursorPosition(safePosition);
      pendingCursorPosition.current = safePosition;
    };

    loadExpression();
  }, [isOpen, selectedVariable?.id, customWorkflowVariables, expressionValue, setExpressionValue, isNewWorkflowVariable, workflow?.Id, ensureCursorOutsideChips]);

  /** Sync cursor position after expression changes */
  useEffect(() => {
    if (expressionInputRef.current) {
      const input = expressionInputRef.current;
      let positionToSet;
      let expressionToUse = localExpression;

      if (pendingCursorPosition.current !== null) {
        let pendingPos = pendingCursorPosition.current;
        const { expression: safeExpr, position: safePos } = ensureCursorOutsideChips(localExpression, pendingPos);
        if (safeExpr !== localExpression) {
          expressionToUse = safeExpr;
          setLocalExpression(safeExpr);
        }
        positionToSet = safePos;
        pendingCursorPosition.current = null;
      } else {
        positionToSet = localExpression.length;
        
        const { expression: updatedExpression, position: updatedPosition } = ensureSpacesBeforeCursor(
          localExpression,
          positionToSet
        );
        
        if (updatedExpression !== localExpression) {
          expressionToUse = updatedExpression;
          positionToSet = updatedPosition;
          setLocalExpression(updatedExpression);
        }
      }

      requestAnimationFrame(() => {
        setTimeout(() => {
          if (input && input === expressionInputRef.current) {
            const maxPosition = expressionToUse.length;
            const finalPosition = Math.min(positionToSet, maxPosition);

            input.setSelectionRange(finalPosition, finalPosition);
            input.focus();
            setIsFocused(true);
            setCursorPosition(finalPosition);

            setTimeout(() => {
              if (input && input === expressionInputRef.current) {
                const currentPos = input.selectionStart || 0;
                if (currentPos !== finalPosition) {
                  input.setSelectionRange(finalPosition, finalPosition);
                  input.focus();
                  setCursorPosition(finalPosition);
                }
              }
            }, number.TEN);
          }
        }, number.ZERO);
      });
    }
  }, [localExpression, ensureSpacesBeforeCursor]);

  /** Insert text into expression at cursor position */
  const insertTextAtCursor = useCallback((textToInsert) => {
    const input = expressionInputRef.current;
    if (!input) return;

    input.focus();

    const currentPos = getCurrentCursorPosition();
    const before = localExpression.substring(0, currentPos);
    const after = localExpression.substring(currentPos);
    const newExpression = before + textToInsert + after;
    const newPosition = currentPos + textToInsert.length;

    setLocalExpression(newExpression);
    setCursorPosition(newPosition);
    markExpressionChanged();
    pendingCursorPosition.current = newPosition;
  }, [localExpression, getCurrentCursorPosition, markExpressionChanged]);

  /** Handle smart chip click */
  const handleSmartChipClick = (chip) => {
    const currentPos = getCurrentCursorPosition();
    const needsSpaceBefore = currentPos > 0;
    const chipText = needsSpaceBefore ? ` [${chip.name}]  ` : `[${chip.name}]  `;
    insertTextAtCursor(chipText);
  };

  /** Handle operator click */
  const handleOperatorClick = (operator) => {
    const operatorText = ` ${operator.symbol}  `;
    insertTextAtCursor(operatorText);
  };

  /** Handle expression input change - allow all typing including text and numbers */
  const handleExpressionChange = useCallback((e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart !== null ? e.target.selectionStart : 0;

    setLocalExpression(value);
    setCursorPosition(cursorPos);
    markExpressionChanged();
    
    if (!value.trim()) {
      handleEmptyExpression();
    } else {
      setErrorMessage("");
    }

    requestAnimationFrame(() => {
      if (expressionInputRef.current && expressionInputRef.current === e.target) {
        expressionInputRef.current.setSelectionRange(cursorPos, cursorPos);
      }
    });
  }, []);

  /** Handle expression input click */
  const handleExpressionClick = (e) => {
    setTimeout(() => {
      const input = expressionInputRef.current;
      if (input) {
        const pos = input.selectionStart !== null ? input.selectionStart : 0;
        setCursorPosition(pos);
        input.focus();
        setIsFocused(true);
      }
    }, number.ZERO);
  };

  /** Handle expression input key events */
  const handleExpressionKeyDown = (e) => {
    const input = e.target;
    const cursorPos = input.selectionStart || 0;

    const navigationKeys = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "Tab", "PageUp", "PageDown"];
    if (navigationKeys.includes(e.key)) {
      setTimeout(() => {
        if (input && input === expressionInputRef.current) {
          const newPos = input.selectionStart !== null ? input.selectionStart : 0;
          setCursorPosition(newPos);
        }
      }, number.ZERO);
      return;
    }

    const isControlKey = e.ctrlKey || e.metaKey;
    if (isControlKey) {
      return;
    }

    if (e.key === "Backspace" && cursorPos > 0) {
      let itemToRemove = findItemAtPosition(cursorPos);
      
      if (!itemToRemove || (itemToRemove.type !== label.SMARTCHIP && itemToRemove.type !== label.OPERATOR)) {
        itemToRemove = findItemAtPosition(cursorPos - 1);
      }
      
      if (itemToRemove && (itemToRemove.type === label.SMARTCHIP || itemToRemove.type === label.OPERATOR)) {
        e.preventDefault();
        removeItem(itemToRemove.start, itemToRemove.value.length);
        return;
      }

      const expressionAfterBackspace = localExpression.substring(0, cursorPos - 1) + localExpression.substring(cursorPos);
      if (!expressionAfterBackspace.trim()) {
        handleEmptyExpression();
      }

      markExpressionChanged();
    } else if (e.key === "Delete" && cursorPos < localExpression.length) {
      const itemToRemove = findItemAtPosition(cursorPos);
      if (itemToRemove && (itemToRemove.type === label.SMARTCHIP || itemToRemove.type === label.OPERATOR)) {
        e.preventDefault();
        removeItem(itemToRemove.start, itemToRemove.value.length);
        return;
      }

      markExpressionChanged();
    } else {
      markExpressionChanged();
    }
  };

  /** Find item at a specific position in the expression - uses memoized expressionItems */
  const findItemAtPosition = useCallback((position) => {
    let item = expressionItems.find((item) => position >= item.start && position < item.end);
    
    if (!item && position > 0) {
      const chipsAndOperators = expressionItems.filter(
        (item) => item.type === label.SMARTCHIP || item.type === label.OPERATOR
      );
      item = chipsAndOperators.find((item) => {
        return position >= item.end && position <= item.end + number.TWO;
      });
    }
    
    return item;
  }, [expressionItems]);

  /** Helper function to detect hover over expression items */
  const handleHoverDetection = (clientX, clientY) => {
    const elementAtPoint = document.elementFromPoint(clientX, clientY);

    const expressionItem = elementAtPoint?.closest("[data-item-index]");

    if (expressionItem) {
      const index = parseInt(expressionItem.getAttribute("data-item-index"));
      const item = expressionItems[index];
      if (item && (item.type === label.SMARTCHIP || item.type === label.OPERATOR)) {
        setHoveredItemIndex(index);
        return;
      }
    }

    const removeButton = elementAtPoint?.closest(".expression-item-remove");
    if (removeButton) {
      const parentItem = removeButton.closest("[data-item-index]");
      if (parentItem) {
        const index = parseInt(parentItem.getAttribute("data-item-index"));
        const item = expressionItems[index];
          if (item && (item.type === label.SMARTCHIP || item.type === label.OPERATOR)) {
          setHoveredItemIndex(index);
          return;
        }
      }
    }

    setHoveredItemIndex(null);
  };

  /** Remove item from expression */
  const removeItem = useCallback((index, length) => {
    const before = localExpression.substring(0, index);
    const after = localExpression.substring(index + length);
    const newExpression = before + after;
    setLocalExpression(newExpression);
    markExpressionChanged();
    
    if (!newExpression.trim()) {
      handleEmptyExpression();
    }

    setTimeout(() => {
      if (expressionInputRef.current) {
        const newPosition = Math.min(index, newExpression.length);
        expressionInputRef.current.setSelectionRange(newPosition, newPosition);
        expressionInputRef.current.focus();
      }
    }, number.ZERO);
  }, [localExpression, handleEmptyExpression, markExpressionChanged]);

  /** Normalize expression by removing extra spaces */
  const normalizeExpression = useCallback((expression) => {
    if (!expression || !expression.trim()) return expression || "";

    let normalized = expression
      .replace(/\]\s*([+\-*/=])/g, "]   $1")
      .replace(/\]\s*\[/g, "]   [")
      .replace(/([+\-*/=])\s*\[/g, "$1   [")
      .replace(/([+\-*/=])\s*([+\-*/=])/g, "$1   $2");

    normalized = normalized.replace(/ {4,}/g, "   ");
    normalized = normalized.trim();

    return normalized;
  }, []);

  /** Validate expression */
  const validateExpression = useCallback(() => {
    if (!localExpression.trim()) {
      setIsValid(true);
      setErrorMessage("");
      return true;
    }

    const chips = expressionItems.filter((item) => item.type === label.SMARTCHIP);
    const operators = expressionItems.filter((item) => item.type === label.OPERATOR);

    if (chips.length === 0) {
      setValidationError();
      return false;
    }

    if (currentVariableName) {
      const usesCurrentVariable = chips.some((chip) => chip.name === currentVariableName);
      if (usesCurrentVariable) {
        setValidationError();
        return false;
      }
    }

    for (const chip of chips) {
      const chipVariable = availableSmartChips.find((v) => v.name === chip.name);
      if (!chipVariable) {
        setValidationError();
        return false;
      }

      const chipType = chipVariable.type || chipVariable.variableType;
      if (chipType?.toLowerCase() !== expectedVariableType?.toLowerCase()) {
        setValidationError();
        return false;
      }
    }

    const nonTextItems = expressionItems.filter((item) => item.type !== label.TEXT || item.value.trim() !== "");

    if (nonTextItems.length > 0) {
      const firstItem = nonTextItems[0];
      const lastItem = nonTextItems[nonTextItems.length - 1];

        if (firstItem.type === label.OPERATOR) {
        setValidationError();
        return false;
      }

      if (lastItem.type === label.OPERATOR) {
        setValidationError();
        return false;
      }
    }

    for (let i = 0; i < nonTextItems.length - 1; i++) {
      const current = nonTextItems[i];
      const next = nonTextItems[i + 1];

      if (current.type === label.SMARTCHIP && next.type === label.SMARTCHIP) {
        setValidationError();
        return false;
      }

      if (current.type === label.OPERATOR && next.type === label.OPERATOR) {
        setValidationError();
        return false;
      }
    }

    if (chips.length > 1 && operators.length === 0) {
      setValidationError();
      return false;
    }

    if (operators.length > 0 && chips.length < 2) {
      setValidationError();
      return false;
    }

    setIsValid(true);
    setErrorMessage("");
    return true;
  }, [localExpression, expressionItems, currentVariableName, availableSmartChips, expectedVariableType, setValidationError]);

  /** Handle validate and save */
  const handleValidateAndSave = async () => {
    if (!localExpression.trim()) {
      handleEmptyExpression();
    }
    
    const validationResult = validateExpression();

    if (validationResult) {
      const normalizedExpression = normalizeExpression(localExpression);
      setExpressionValue(normalizedExpression);
      setHasVarChanges(true);
      setLocalExpression(normalizedExpression);
      
      const endPosition = normalizedExpression.length;
      setCursorPosition(endPosition);
      pendingCursorPosition.current = endPosition;

      flushSync(() => {
        setIsValidated(true);
        setHasChanges(false);
        setIsValid(true);
        setErrorMessage("");
      });

      let workflowVariableId = selectedVariable?.id;

      const currentId = selectedVariable?.id;
      const isTemporaryId = typeof currentId === "number";
      const needsToSaveFirst = workflow?.Id && onSaveVariable && (isNewWorkflowVariable || isTemporaryId);

      if (needsToSaveFirst) {
        const savedVariable = await onSaveVariable();

        if (savedVariable) {
          const savedId = savedVariable.id || savedVariable.Id;
          if (savedId) {
            workflowVariableId = savedId;
            if (setSelectedVariable) {
              setSelectedVariable(savedVariable);
            }
          }
        } else {
          const updatedId = selectedVariable?.id;
          if (updatedId && typeof updatedId === "string") {
            workflowVariableId = updatedId;
          } else {
            return;
          }
        }
      }

      if (workflowVariableId && workflow?.Id && user?.id) {
        const expressionWithIds = convertExpressionNamesToIds(normalizedExpression, customWorkflowVariables);

        const payload = {
          workflowId: workflow.Id,
          workflowVariableId: workflowVariableId,
          expression: expressionWithIds,
          userId: user.id,
        };
        if (formId) {
          payload.formId = formId;
        }

        await addOrUpdateWorkflowExpression(payload);
      }
    }
  };

  /** Handle close */
  const handleClose = () => {
    if (hasChanges && !isValidated) {
      setShowCloseConfirmDialog(true);
      return;
    }

    if (isValidated && isValid) {
      if (onSave) {
        onSave(localExpression);
      }
    }

    onClose();
  };

  /** Handle close confirmation */
  const handleCloseConfirm = () => {
    setShowCloseConfirmDialog(false);
    onClose();
  };

  /** Handle close cancel */
  const handleCloseCancel = () => {
    setShowCloseConfirmDialog(false);
  };


  return (
    <Dialog
      title={WORKFLOW_VARIABLE_LABELS.EXPRESSION_EDITOR}
      onClose={handleClose}
      width={"100%"}
      height={"100%"}
      className='expression-editor-dialog'>
      <div className='expression-editor-container d-flex h-100'>
        {/* Left Panel - Smart Chips */}
        <div className='expression-editor-panel expression-editor-smart-chips'>
          <div className='expression-editor-panel-header'>
            <h4>{label.SMART_CHIPS}</h4>
          </div>
          <div className='expression-editor-search'>
            <Input placeholder={placeholder.SEARCH} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
          <div className='expression-editor-list'>
            {filteredSmartChips?.map((chip) => {
              const chipDescription = chip?.variableId?.description || chip?.description;
              return (
                <div key={chip.id} className='expression-editor-chip-item cursor-pointer' onClick={() => handleSmartChipClick(chip)}>
                  <div className='d-flex align-items-center gap-2'>
                    <span>
                      {chip?.type === WORKFLOW_VARIABLE_LABELS.DROPDOWN
                        ? chip?.attributes?.numberOnly
                          ? VARIABLE_ICONS.number_dropdown
                          : VARIABLE_ICONS.dropdown
                        : VARIABLE_ICONS[chip.type]}
                    </span>
                    <div className='d-flex flex-column'>
                      <span>{chip.name}</span>
                      {chipDescription && <span className='small text-muted'>{chipDescription}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
            {filteredSmartChips.length === number.ZERO && <div className='text-muted text-center p-3'>{label.EXPRESSION_EDITOR_NO_SMART_CHIPS}</div>}
          </div>
        </div>

        {/* Middle Panel - Expression */}
        <div className='expression-editor-panel expression-editor-expression flex-grow-1 d-flex flex-column'>
          <div className='expression-editor-panel-header'>
            <h4>{label.EXPRESSION}</h4>
          </div>
          <div className='flex-grow-1 d-flex flex-column' style={{ minHeight: 0 }}>
            <div
              ref={containerRef}
              className={`expression-editor-input-container flex-grow-1 ${isFocused ? "is-focused" : ""}`}
              onClick={(e) => {
                if (e.target.classList.contains("expression-item-remove") || e.target.closest(".expression-item")) {
                  return;
                }

                if (expressionInputRef.current) {
                  const textarea = expressionInputRef.current;
                  textarea.focus();
                  setIsFocused(true);

                  setTimeout(() => {
                    if (textarea && expressionInputRef.current === textarea) {
                      const pos = textarea.selectionStart !== null ? textarea.selectionStart : 0;
                      setCursorPosition(pos);
                    }
                  }, number.ZERO);
                }
              }}
              onMouseMove={(e) => {
                handleHoverDetection(e.clientX, e.clientY);
              }}
              onMouseLeave={() => setHoveredItemIndex(null)}>
              <div className='expression-editor-display'>
                {!localExpression ? (
                  <div className='expression-editor-placeholder'>{label.EXPRESSION_EDITOR_CLICK_TO_START}</div>
                ) : (
                  expressionItems.map((item, index) => {
                    if (item.type === label.TEXT) {
                      return (
                        <span key={index} style={whiteSpacePreStyle}>
                          {item.value}
                        </span>
                      );
                    }
                    
                    const isSmartChip = item.type === label.SMARTCHIP;
                    const isHovered = hoveredItemIndex === index;
                    
                    return (
                      <span
                        key={index}
                        data-item-index={index}
                        className={`expression-item expression-item-${item.type}${isHovered ? " hovered" : ""}`}
                        onMouseEnter={() => setHoveredItemIndex(index)}
                        onMouseLeave={() => setHoveredItemIndex(null)}>
                        {isSmartChip ? (
                          <>
                            <span style={hiddenStyle}>[</span>
                            {item.name}
                            <span style={hiddenStyle}>]</span>
                          </>
                        ) : (
                          item.value
                        )}
                        <span
                          className='expression-item-remove'
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            e.nativeEvent?.stopImmediatePropagation();
                            removeItem(item.start, item.value.length);
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            e.nativeEvent?.stopImmediatePropagation();
                          }}
                          onMouseUp={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            e.nativeEvent?.stopImmediatePropagation();
                          }}>
                          ×
                        </span>
                      </span>
                    );
                  })
                )}
              </div>
              <textarea
                ref={expressionInputRef}
                className='expression-editor-input'
                value={localExpression}
                onChange={handleExpressionChange}
                onClick={(e) => {
                  if (e.target.closest(".expression-item-remove")) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  handleExpressionClick(e);
                }}
                onKeyDown={handleExpressionKeyDown}
                onMouseMove={(e) => {
                  handleHoverDetection(e.clientX, e.clientY);
                }}
                onFocus={() => {
                  setHoveredItemIndex(null);
                  setIsFocused(true);
                }}
                onBlur={() => {
                  setIsFocused(false);
                }}
                rows={number.EIGHT}
              />
            </div>
          </div>
          <div className='expression-editor-actions d-flex justify-content-between align-items-center mt-3' style={{ flexShrink: 0 }}>
            <div>{errorMessage && <span className='text-danger'>{errorMessage}</span>}</div>
            <div className='d-flex gap-2'>
              <Button onClick={handleClose}>{label.CLOSE}</Button>
              <Button className='btn-primary btn ' onClick={handleValidateAndSave} disabled={isValidated && !hasChanges}>
                {label.EXPRESSION_EDITOR_VALIDATE_AND_SAVE}
              </Button>
            </div>
          </div>
        </div>

        {/* Right Panel - Operators */}
        <div className='expression-editor-panel expression-editor-operators'>
          <div className='expression-editor-panel-header'>
            <h4>{label.OPERATORS}</h4>
          </div>
          <div className='expression-editor-operators-section'>
            <div className='small text-muted mb-2'>{label.EXPRESSION_EDITOR_MATHEMATICAL_OPERATORS}</div>
            <div className='small text-muted mb-3'>{label.EXPRESSION_EDITOR_OPERATORS_DESCRIPTION}</div>
            <div className='expression-editor-operators-list'>
              {operators.map((operator) => (
                <div key={operator.id} className='expression-editor-operator-item cursor-pointer' onClick={() => handleOperatorClick(operator)}>
                  <div className='d-flex align-items-center gap-2'>
                    <span className='expression-operator-icon'>{operator.symbol}</span>
                    <span>{operator.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Close Confirmation Dialog */}
      {showCloseConfirmDialog && (
        <Dialog title={label.CONFIRM} onClose={handleCloseCancel} className='unsaved-changes-dialog'>
          <p className='confirmation-dialog-message'>{WORKFLOW_VARIABLE_LABELS.UNSAVED_CHANGES_WARNING}</p>
          <DialogActionsBar>
            <Button themeColor='primary' onClick={handleCloseConfirm}>
              {label.CLOSE}
            </Button>
            <Button themeColor='base' onClick={handleCloseCancel}>
              {label.CANCEL}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </Dialog>
  );
};

export default ExpressionEditor;
